import type { CalendarParsedAttendee } from '../calendar-domain.js';

const CALENDAR_POINTER_MINUTE_STEP = 15;

export function pointerMinuteWithinHour(
  pointer: PointerEvent,
  target: EventTarget | null
): number {
  if (!(target instanceof HTMLElement)) return 0;
  const rect = target.getBoundingClientRect();
  const ratio = rect.height ? (pointer.clientY - rect.top) / rect.height : 0;
  const raw = Math.max(0, Math.min(59, Math.floor(ratio * 60)));
  return (
    Math.floor(raw / CALENDAR_POINTER_MINUTE_STEP) *
    CALENDAR_POINTER_MINUTE_STEP
  );
}

export function pointerMinuteWithinDay(
  pointer: { clientY: number },
  target: EventTarget | null
): number {
  if (!(target instanceof HTMLElement)) return 0;
  const rect = target.getBoundingClientRect();
  const ratio = rect.height ? (pointer.clientY - rect.top) / rect.height : 0;
  const raw = Math.max(0, Math.min(24 * 60 - 1, Math.floor(ratio * 24 * 60)));
  return (
    Math.floor(raw / CALENDAR_POINTER_MINUTE_STEP) *
    CALENDAR_POINTER_MINUTE_STEP
  );
}

export function pointerMinuteOffsetWithinDuration(
  pointer: { clientY: number },
  target: EventTarget | null,
  durationMinutes: number
): number {
  if (!(target instanceof HTMLElement)) return 0;
  const rect = target.getBoundingClientRect();
  const ratio = rect.height ? (pointer.clientY - rect.top) / rect.height : 0;
  const raw = Math.max(0, Math.min(durationMinutes, durationMinutes * ratio));
  return (
    Math.round(raw / CALENDAR_POINTER_MINUTE_STEP) *
    CALENDAR_POINTER_MINUTE_STEP
  );
}

export function capitalize(value: string): string {
  return `${value.slice(0, 1).toUpperCase()}${value.slice(1)}`;
}

export function guessTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

export function parseCalendarCursorTime(
  value: string | null
): number | undefined {
  if (value === null || value.trim() === '') return undefined;
  const cursorTime = Number(value);
  return Number.isFinite(cursorTime) ? cursorTime : undefined;
}

export function formatAttendeeToken(attendee: CalendarParsedAttendee): string {
  const identity = attendee.email
    ? `${attendee.name ?? attendee.email} <${attendee.email}>`
    : (attendee.name ?? attendee.key);
  return attendee.status === 'needs_action'
    ? identity
    : `${identity} [${attendee.status}]`;
}

export function disposeCalendarSyncPriority(disposable: unknown): void {
  if (typeof disposable === 'function') {
    disposable();
    return;
  }
  const object = disposable as
    | {
        dispose?: unknown;
        unsubscribe?: unknown;
      }
    | null
    | undefined;
  if (typeof object?.dispose === 'function') {
    object.dispose();
    return;
  }
  if (typeof object?.unsubscribe === 'function') {
    object.unsubscribe();
  }
}

export function compactRecord<T extends Record<string, string | undefined>>(
  value: T
): T {
  const result = {} as T;
  for (const [key, item] of Object.entries(value)) {
    if (item) {
      result[key as keyof T] = item as T[keyof T];
    }
  }
  return result;
}

export function nullableText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

export function randomRequestId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
}

export function stableSequence(parts: readonly string[]): number {
  let hash = 0;
  for (const part of parts) {
    for (let index = 0; index < part.length; index += 1) {
      hash = (hash * 31 + part.charCodeAt(index)) >>> 0;
    }
  }
  return hash;
}
